import _ from 'underscore';

import { AssetsRecommendationData, IServerContext, DisplayAdsRecommendationData } from '../../types';

export function getCommonOdataOptions(serverContext: IServerContext) {
  const {
    CurrentCustomer: currentCustomer,
    CurrentAccount: currentAccount,
    ServicesEndpoints: { CampaignAPI: host },
    CampaignAPIPath: apiPath,
    Token: token,
    TokenType: tokenType,
  } = serverContext;

  const isOnebox = host && host.toString().indexOf('redmond.corp') > 0;
  const path = apiPath || isOnebox ? '/Advertiser/V2' : '/Campaign/V2';
  return {
    cid: currentCustomer?.Id,
    aid: currentAccount?.Id,
    host,
    path,
    token,
    tokenType,
  };
}

export const mapThemeAsset = (asserts: any) => {
  const {
    Themes,
    ImageAssets,
    Logo,
    Cta,
  } = asserts;

  const mappedTheme = _.chain(Themes).map(({
    Name,
    ModelSource,
    RecommendationId,
    ImageIndices,
  }) => ({
    Name,
    ModelSource,
    RecommendationId,
    Images: _.map(ImageIndices, (index: number) => ImageAssets[index]),
  })).value();

  return {
    Themes: mappedTheme,
    ImageAssets,
    Logo,
    Cta,
  };
};

/**
 * Takes the assets recommendation response and converts the asset indices into the actual asset objects.
 * @param assets - The assets recommendation response.
 * @returns Formatted object with actual assets.
 */
export const mapAssetRecommendation = (assets: any): AssetsRecommendationData => {
  const { RecommendedAssets = {}, Logo, Cta } = assets;
  const {
    Themes,
    ImageAssets,
    ShortHeadlines,
    LongHeadlines,
    Descriptions,
  } = RecommendedAssets;

  const mappedTheme = _.chain(Themes).map(({
    Name,
    RecommendationId,
    ImageIndices,
    ShortHeadlineIndices,
    LongHeadlineIndices,
    DescriptionIndices,
  }) => ({
    Name,
    RecommendationId,
    Images: _.map(ImageIndices, (index: number) => ImageAssets[index]),
    ShortHeadlines: _.map(ShortHeadlineIndices, (index: number) => ShortHeadlines[index]).filter((asset: any) => asset && asset.Text),
    LongHeadlines: _.map(LongHeadlineIndices, (index: number) => LongHeadlines[index]).filter((asset: any) => asset && asset.Text),
    Descriptions: _.map(DescriptionIndices, (index: number) => Descriptions[index]).filter((asset: any) => asset && asset.Text),
  })).value();

  return {
    RecommendedAssets: {
      Themes: mappedTheme,
      ImageAssets,
      ShortHeadlines,
      LongHeadlines,
      Descriptions,
    },
    Logo,
    Cta,
  };
};

export const mapDisplayAdsRecommendation = (response : any): DisplayAdsRecommendationData => {
  // transform response to expected format
  const ads = response?.data?.ads;
  const mappedAds = _.map(ads, (ad: any) => {
    const { recommendationId, url, template } = ad;
    const {
      image,
    } = template;
    const { url: imageUrl } = image;
    return {
      recommendationId,
      url,
      template,
      image: imageUrl,
    };
  });

  // get unique images
  const uniqueImages = _.chain(mappedAds)
    .uniq(false, (mappedAd) => mappedAd.image)
    .map((mappedAd) => ({
      fullUrl: mappedAd.image,
      thumbnailUrl: mappedAd.image,
      suggestionId: mappedAd.recommendationId,
    }))
    .value();

  return {
    Ads: mappedAds,
    ImageAssets: uniqueImages,
  };
};

export const mapToUserPrompt = (prompts: string[]) => (!_.isEmpty(prompts) ? {
  '@odata.type': '#Model.AssetRecommendationUserPrompt',
  Prompts: prompts,
} : undefined);

export const mapToThemes = (themes: string[]) => (!_.isEmpty(themes) ? themes.map((theme) => ({
  '@odata.type': '#Model.RequestedTheme',
  Name: theme,
})) : undefined);

export const mapToAsset = (shortHeadlines: string[], longHeadlines: string[], descriptions: string[]) => ({
  '@odata.type': '#Model.AssetRecommendationInfo',
  ShortHeadlines: !_.isEmpty(shortHeadlines) ? shortHeadlines.map((shortHeadline) => ({
    '@odata.type': '#Model.AssetRecommendationTextAsset',
    Text: shortHeadline,
  })) : undefined,
  LongHeadlines: !_.isEmpty(longHeadlines) ? longHeadlines.map((longHeadline) => ({
    '@odata.type': '#Model.AssetRecommendationTextAsset',
    Text: longHeadline,
  })) : undefined,
  Descriptions: !_.isEmpty(descriptions) ? descriptions.map((description) => ({
    '@odata.type': '#Model.AssetRecommendationTextAsset',
    Text: description,
  })) : undefined,
});
